import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NgIf, NgFor } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { OrganizationDetails } from '../../../models/organization-details';
import { Country } from '../../../models/v2/country';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { UNDERSCORE } from '../../../../shared/utils/constant';

export interface OrganizationCardDetailsIdentityForm {
	name: string;
	vatNumber: string;
	address: string;
	postCode: string;
	city: string;
	stateOrCounty: string;
	country: Country;
}

@Component({
	selector: 'copo-organization-card-details-identity',
	templateUrl: './organization-card-details-identity.component.html',
	styleUrl: './organization-card-details-identity.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		NgIf,

		MatInputModule,
		MatIconModule,

		MatSelectModule,
		NgFor,

		MatOptionModule,
		LoadingSpinnerComponent
	]
})
export class OrganizationCardDetailsIdentityComponent implements OnChanges, OnDestroy {
	@Input() organization?: OrganizationDetails;
	@Input() editMode = false;
	@Output() readonly organizationCardDetailsIdentityChange =
		new EventEmitter<OrganizationCardDetailsIdentityForm>(true);

	@Output() readonly formCheck = new EventEmitter<boolean>(false);
	@Input() supplier?: string[];
	@Input() countries?: Country[];

	orgForm!: FormGroup;
	readonly UNDERSCORE = UNDERSCORE;
	private readonly orgFormSub: Subscription;

	constructor() {
		this.orgForm = new FormGroup({
			name: new FormControl('', Validators.required),
			vatNumber: new FormControl('', Validators.required),
			address: new FormControl(''),
			postCode: new FormControl(''),
			city: new FormControl(''),
			stateOrCounty: new FormControl(''),
			country: new FormControl('')
		});
		this.orgFormSub = this.orgForm.valueChanges.subscribe({
			next: form => {
				this.organizationCardDetailsIdentityChange.emit(<OrganizationCardDetailsIdentityForm>form);
				this.formCheckOrg(this.orgForm.invalid);
			},
			error: (err: unknown) => {
				console.error('Error while form value changes', err);
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.orgForm.markAllAsTouched();
		if ('countries' in changes) {
			this.countries = <Country[] | undefined>changes['countries'].currentValue;
		}
		this.updateOrgForm();
		if (this.editMode && this.organization && this.countries) {
			for (const country of this.countries) {
				if (country.countryDescription === this.organization.address.countryCodeDescription) {
					this.orgForm.get('country')?.setValue(country);
				}
			}
		}
	}

	ngOnDestroy() {
		this.orgFormSub.unsubscribe();
	}

	updateOrgForm() {
		if (!this.organization) {
			return;
		}
		this.orgForm.markAllAsTouched();
		this.orgForm.patchValue(<OrganizationCardDetailsIdentityForm>{
			name: this.organization.name,
			vatNumber: this.organization.vatNumber,
			address: this.organization.address.address,
			postCode: this.organization.address.zipCode,
			city: this.organization.address.city,
			stateOrCounty: this.organization.address.county,
			country: {
				countryCode: this.organization.address.countryCode,
				countryDescription: this.organization.address.countryCodeDescription
			}
		});
	}

	formCheckOrg(formValidation: boolean): void {
		this.formCheck.emit(formValidation);
	}
}
